// A read-only editing scene, not a simulated visit to the middle of a flow.
// The real selected SDK renders a copy of the selected question. No prior answers
// or traversal history are invented, and the original package is never changed.
export function createQuestionScene(pkg, selection, variant = 'A') {
  const original = selection?.step;
  if (!original) return { reason: 'Select a question to see it here.' };
  if (!['text', 'email', 'phone', 'single_select', 'multi_select', 'scheduler', 'message'].includes(original.step_type)) {
    return { reason: 'This question type cannot be shown on its own. Switch to Test to inspect the full path.' };
  }
  if (!original.step_prompts?.some(prompt => String(prompt || '').trim())) return { reason: 'Add the question or message wording to see it here.' };
  if (['text', 'email', 'phone', 'scheduler'].includes(original.step_type) && !original.step_columns?.length) {
    return { reason: 'Choose where this answer is saved to preview the question.' };
  }
  if (['single_select', 'multi_select'].includes(original.step_type) &&
    (!original.step_options?.length || original.step_options.some(option => !String(option.label || '').trim()))) {
    return { reason: 'Give each choice a label to preview this question.' };
  }
  const next = JSON.parse(JSON.stringify(variant === 'B' && pkg.mag_ab?.b ? { ...pkg, ...pkg.mag_ab.b } : pkg));
  const step = JSON.parse(JSON.stringify(original));
  // Routing cannot be exercised in a read-only isolated scene. Removing outgoing
  // references from this COPY avoids declaring their absent destinations broken.
  step.step_options = (step.step_options || []).map(option => ({ ...option, goto: '' }));
  if (step.step_type === 'message' && !step.step_config?.terminal) {
    step.step_config = { ...step.step_config, terminal: true };
    delete step.step_config.cta; // Do not reveal a dormant terminal button.
  }
  // Keep a grouped screen intact while editing one of its fields.
  const authored = selection.route && selection.route !== 'chat' ? next.mag_routes?.[selection.route] : next.mag_macro_steps;
  const selectedIndex = authored?.findIndex(item => String(item.id) === String(original.id)) ?? -1;
  let group = [step];
  if (selectedIndex >= 0) {
    let first = selectedIndex, last = selectedIndex;
    while (first > 0 && authored[first].step_config?.group_with_previous) first--;
    while (last + 1 < authored.length && authored[last + 1].step_config?.group_with_previous) last++;
    if (last > first) {
      group = authored.slice(first,last+1);
      group[selectedIndex-first] = step;
    }
  }
  // A standalone copy has no predecessor, even if the draft currently does.
  group[0].step_config = {...group[0].step_config}; delete group[0].step_config.group_with_previous;
  if (!Object.keys(group[0].step_config).length) delete group[0].step_config;
  next.mag_macro_steps = group; next.mag_routes = {}; next.mag_actions = [];
  next.mag_home = false; next.mag_summary = false; next.mag_ab = null;
  return { pkg: next, pausedMessage: step.step_type === 'message' && !original.step_config?.terminal };
}

// Serialized into the preview only. This presentation layer does not alter the
// selected SDK, its source, or any public runtime behavior on installed websites.
export function installQuestionScene({ token, unavailable = false, selectedStepId = null }) {
  let stopped = false, attempts = 0, timer, observer, shadow;
  const send = kind => { if (!stopped) window.parent.postMessage({ type: 'm5m-studio-preview', token, kind }, '*'); };
  const block = event => {
    if (event.type === 'keydown' && event.key === 'Tab') return;
    const wording = event.composedPath().find(node => node.matches?.('[data-m5m-edit-wording]'));
    if (wording && (event.type === 'click' || (event.type === 'keydown' && ['Enter', ' '].includes(event.key)))) send('edit-wording');
    event.preventDefault(); event.stopImmediatePropagation();
  };
  const start = () => {
    // Only the authored wording is interactive. Visitor controls remain inert,
    // and capture guards also prevent synthetic answer/submission events.
    for (const type of ['click', 'submit', 'keydown', 'beforeinput']) document.addEventListener(type, block, true);
    if (unavailable) { send('ready'); return; }
    const find = () => {
      if (stopped) return;
      shadow = document.querySelector('[id^="m5m-magnet-"]')?.shadowRoot;
      if (!shadow || !window.MachFiveMagnet?.instances?.length) {
        if (++attempts < 200) timer = setTimeout(find, 40);
        else send('failed');
        return;
      }
      for (const type of ['click', 'submit', 'keydown', 'beforeinput']) shadow.addEventListener(type, block, true);
      const style = document.createElement('style');
      style.textContent = '.m5m-progress,.m5m-back{display:none!important} [data-m5m-edit-wording]{cursor:pointer;border-radius:4px} [data-m5m-edit-wording]:hover,[data-m5m-edit-wording]:focus-visible{outline:2px solid #3b9de0;outline-offset:4px}';
      shadow.append(style);
      const prepare = () => {
        shadow.querySelectorAll('button,input,select,textarea,a,summary').forEach(control => { control.inert = true; });
        const prompts = shadow.querySelector('.m5m-group-form')
          ? [...shadow.querySelectorAll('.m5m-group-field')].filter(field => field.dataset.stepId === String(selectedStepId)).map(field => field.querySelector('.m5m-group-label'))
          : shadow.querySelectorAll('.m5m-bot');
        prompts.forEach(prompt => {
          prompt.dataset.m5mEditWording = ''; prompt.setAttribute('role', 'button'); prompt.tabIndex = 0;
          prompt.setAttribute('aria-label', `Edit wording: ${prompt.textContent}`); prompt.title = 'Edit this wording';
        });
      };
      observer = new MutationObserver(prepare); observer.observe(shadow, { childList: true, subtree: true });
      window.MachFiveMagnet.open();
      prepare();
      const ready = () => {
        if (stopped) return;
        if (shadow.querySelector('.m5m-root.m5m-open') &&
          shadow.querySelector('.m5m-form,.m5m-choice,.m5m-multi,.m5m-sched,.m5m-bot,.m5m-error')) send('ready');
        else if (++attempts < 200) timer = setTimeout(ready, 40);
        else send('failed');
      };
      ready();
    };
    find();
  };
  window.addEventListener('pagehide', () => {
    stopped = true; clearTimeout(timer); observer?.disconnect();
    for (const type of ['click', 'submit', 'keydown', 'beforeinput']) { document.removeEventListener(type, block, true); shadow?.removeEventListener(type, block, true); }
  }, { once: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
